<template>
    <section v-if="isAccessoryType" id="matchingEquipmentDetail" class="descriptionContainer">
        <button
            class="btn btn-block text-left descriptionButton"
            :class="{ collapsed: !isOpen }"
            :aria-expanded="isOpen ? 'true' : 'false'"
            aria-controls="matchingEquipment"
            :aria-label="$translate('biokinderDesign::Template.itemAccessories')"
            click-count
            @click="toggleAccordion">
            <svg viewBox="0 0 24 24" width="22" height="22" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round" class="icon-accessories"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
            <span v-html="$translate('biokinderDesign::Template.itemAccessories')"></span>
            <i data-feather="plus"></i>
        </button>

        <div
            id="matchingEquipment"
            class="collapse mt-1"
            :class="{ show: isOpen }"
            aria-labelledby="matchingEquipment"
            data-parent="#rightSide">
            <loading-animation v-if="isLoading"></loading-animation>

            <div v-else-if="loadFailed" class="descriptionContent">
                Zubehör konnte nicht geladen werden.
            </div>

            <div v-else-if="renderedItems.length === 0" class="descriptionContent">
                Kein Zubehör vorhanden.
            </div>

            <category-item
                v-for="item in renderedItems"
                :key="item.variation.id"
                :image-url-accessor="'urlSecondPreview'"
                :item-data="item"
                :view-mode="'cart'"
                :decimal-count="decimalCount">
            </category-item>
        </div>
    </section>

    <section v-else-if="shouldRenderCartCrossSelling" class="cartCrossSelling cross-selling-content">
        <h2><span>Noch mehr entdecken </span>Passend zu diesem Artikel</h2>

        <loading-animation v-if="isLoading"></loading-animation>

        <div v-else-if="loadFailed" class="descriptionContent">
            Empfehlungen konnten nicht geladen werden.
        </div>

        <template v-else>
            <category-item
                v-for="item in renderedItems"
                :key="item.variation.id"
                :image-url-accessor="'urlSecondPreview'"
                :item-data="item"
                :view-mode="'cart'"
                :decimal-count="decimalCount">
            </category-item>
        </template>
    </section>
</template>

<script>
import ApiService from "../../services/ApiService";

export default {
    name: "async-item-list-loader",

    props: {
        type: {
            type: String,
            required: true,
            validator(value)
            {
                return ["accessories", "cartCrossSelling"].includes(value);
            }
        },
        itemId: {
            type: Number,
            default: 0
        },
        variationIds: {
            type: Array,
            default()
            {
                return [];
            }
        },
        decimalCount: {
            type: Number,
            default: 0
        },
        isActive: {
            type: Boolean,
            default: false
        }
    },

    data()
    {
        return {
            isOpen: false,
            renderedItems: [],
            isLoading: false,
            loadFailed: false
        };
    },

    computed: {
        isAccessoryType()
        {
            return this.type === "accessories";
        },

        normalizedVariationIds()
        {
            return [...new Set(
                (this.variationIds || [])
                    .map(value => parseInt(value, 10))
                    .filter(value => value > 0)
            )];
        },

        requestKey()
        {
            if (this.isAccessoryType)
            {
                return this.itemId > 0 ? `accessories:${this.itemId}` : "";
            }

            if (this.normalizedVariationIds.length)
            {
                return `cartCrossSelling:${this.normalizedVariationIds.join(",")}`;
            }

            return "";
        },

        shouldRenderCartCrossSelling()
        {
            return !this.isAccessoryType && (this.isLoading || this.loadFailed || this.renderedItems.length > 0);
        }
    },

    watch: {
        itemId()
        {
            this.isOpen = false;
            this.syncCachedItems();
        },

        requestKey(newValue, oldValue)
        {
            if (newValue !== oldValue)
            {
                this.syncCachedItems();

                if (!this.isAccessoryType && this.isActive)
                {
                    this.ensureItems();
                }
            }
        },

        isActive(newValue)
        {
            if (!this.isAccessoryType && newValue)
            {
                this.ensureItems();
            }
        }
    },

    created()
    {
        this.listCache = new Map();
        this.requestTokenCounter = 0;
    },

    methods: {
        toggleAccordion()
        {
            this.isOpen = !this.isOpen;

            if (this.isOpen)
            {
                this.ensureItems();
            }
        },

        ensureItems()
        {
            if (!this.requestKey)
            {
                this.renderedItems = [];
                this.loadFailed = false;
                return;
            }

            if (this.listCache.has(this.requestKey))
            {
                this.renderedItems = this.listCache.get(this.requestKey);
                this.loadFailed = false;
                return;
            }

            if (this.isLoading)
            {
                return;
            }

            this.fetchItems();
        },

        syncCachedItems()
        {
            if (this.requestKey && this.listCache.has(this.requestKey))
            {
                this.renderedItems = this.listCache.get(this.requestKey);
            }
            else
            {
                this.renderedItems = [];
            }

            this.loadFailed = false;
        },

        fetchItems()
        {
            const token = ++this.requestTokenCounter;
            this.isLoading = true;
            this.loadFailed = false;

            this.createRequest()
                .done(response =>
                {
                    if (token !== this.requestTokenCounter)
                    {
                        return;
                    }

                    const items = this.normalizeResponse(response);
                    this.listCache.set(this.requestKey, items);
                    this.renderedItems = items;
                })
                .fail(() =>
                {
                    if (token === this.requestTokenCounter)
                    {
                        this.loadFailed = true;
                    }
                })
                .always(() =>
                {
                    if (token === this.requestTokenCounter)
                    {
                        this.isLoading = false;
                    }
                });
        },

        createRequest()
        {
            if (this.isAccessoryType)
            {
                return ApiService.get(`/rest/io/items/${this.itemId}/accessories`);
            }

            return ApiService.get("/rest/io/variations", {
                variationIds: this.normalizedVariationIds,
                resultFieldTemplate: "ItemCrossSellingCart"
            });
        },

        normalizeResponse(response)
        {
            const items = this.isAccessoryType
                ? this.normalizeAccessoryResponse(response)
                : this.normalizeVariationResponse(response);

            return items.filter(item =>
            {
                return !!item &&
                    !!item.item &&
                    parseInt(item.item.id, 10) > 0 &&
                    !!item.variation &&
                    parseInt(item.variation.id, 10) > 0 &&
                    !!item.texts &&
                    typeof item.texts.name1 === "string" &&
                    item.texts.name1.trim() !== "" &&
                    item.texts.name1 !== "N / A" &&
                    typeof item.texts.urlPath === "string" &&
                    item.texts.urlPath.trim() !== "" &&
                    !!item.images &&
                    !!item.prices &&
                    !!item.prices.default &&
                    !!item.prices.default.price &&
                    item.prices.default.price.value > 0;
            });
        },

        normalizeAccessoryResponse(response)
        {
            if (Array.isArray(response))
            {
                return response;
            }

            if (Array.isArray(response?.data))
            {
                return response.data;
            }

            return [];
        },

        normalizeVariationResponse(response)
        {
            if (Array.isArray(response?.documents))
            {
                return response.documents.map(document => document.data);
            }

            if (Array.isArray(response?.data?.documents))
            {
                return response.data.documents.map(document => document.data);
            }

            return [];
        }
    }
};
</script>
